/**
 * THE CONFIRMED CARD'S REQUEST — plan-025 A-WAPP (design §36.10; ADR 0077; glossary §6.40 "Confirm card").
 *
 * The one door a confirm card opens: a narrow subclass of the generated client whose only method sends a registry
 * action's request as the signed-in person's own — the same origin, the cookie, the CSRF token and the scope generation
 * the portal's client carries — with `Idempotency-Key: <help_action_id>` and the help action header. The method and the
 * path come from the action registry (the frame's path must be the entry's template with ids in it); the body string is
 * sent as it came, so the api's `sha256(rawBody)` matches the record the help host stored.
 *
 * Nothing else may name the help action header in the portal (`help-account-wall`): only this directory.
 */
import { WeTalkApiClient, type ApiResult } from "@wetalk/api-client";
import { HELP_ACCOUNT_ACTION, HELP_ACTION_HEADER, type HelpAccountActionEntry } from "@wetalk/domain";
import type { HelpAccountActionRequest } from "@wetalk/help-widget";

const ID_SEGMENT = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const UUID = new RegExp(`^${ID_SEGMENT}$`, "u");

/** The entry's path template with ids in its parameters, exactly. */
function matchesPathTemplate(entry: HelpAccountActionEntry, path: string): boolean {
  const pattern = entry.pathTemplate
    .split("/")
    .map((segment) => (/^\{[a-z_]+\}$/u.test(segment) ? ID_SEGMENT : segment.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")))
    .join("/");
  return new RegExp(`^${pattern}$`, "u").test(path);
}

/** Why a card's request is not sent: it is not one the registry would build. */
export class HelpAccountActionRequestRefusedError extends Error {
  override readonly name = "HelpAccountActionRequestRefusedError";
}

/** The body a registry request carries: the JSON object it names, or nothing. */
export function registryRequestBodyOf(request: HelpAccountActionRequest): unknown {
  if (!Object.hasOwn(HELP_ACCOUNT_ACTION, request.action)) throw new HelpAccountActionRequestRefusedError("not a registry action");
  const entry: HelpAccountActionEntry = HELP_ACCOUNT_ACTION[request.action];
  if (request.operation !== entry.operationId || request.method !== entry.method || !matchesPathTemplate(entry, request.path)) {
    throw new HelpAccountActionRequestRefusedError("the request is not the registry's");
  }
  if (request.body === null) return undefined;
  const parsed: unknown = JSON.parse(request.body);
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new HelpAccountActionRequestRefusedError("the body is not an object");
  // Sent as `JSON.stringify(parsed)`: the help host built the string with `JSON.stringify`, so the bytes are the same.
  if (JSON.stringify(parsed) !== request.body) throw new HelpAccountActionRequestRefusedError("the body would not be sent byte for byte");
  return parsed;
}

export class HelpAccountActionClient extends WeTalkApiClient {
  /** Sends one confirmed card's request; answers whatever the status (rejects only when no answer arrived). */
  act(request: HelpAccountActionRequest, helpActionId: string): Promise<ApiResult> {
    if (!UUID.test(helpActionId)) return Promise.reject(new HelpAccountActionRequestRefusedError("a help action id is a UUID"));
    let body: unknown;
    try {
      body = registryRequestBodyOf(request);
    } catch (raised: unknown) {
      return Promise.reject(raised);
    }
    return this.send({
      method: request.method,
      path: request.path,
      query: undefined,
      body,
      header: { "idempotency-key": helpActionId, [HELP_ACTION_HEADER]: helpActionId },
    });
  }
}
